import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { sensitiveViewKey } from '@attendance/shared';
import { useCan } from '../../hooks/usePermission.js';
import { InlineNotice } from './InlineNotice.js';
import { PageShell } from './PageShell.js';

/* ===========================================================================
 * A PAY SCREEN, FOR SOMEONE WHO HAS NOT BEEN GRANTED SALARY ACCESS.
 *
 * Nobody holds `sensitive.salary` by default — Administrator and HR included.
 * The API refuses every pay endpoint without it, so the page would only show
 * a failed request. This says so up front instead, names why, and names the
 * way out (design-rules §3: a refusal is explained; a blank that reads as "no
 * payroll data" is the worst answer). Nothing underneath is mounted, so no
 * request is sent and no figure can flash.
 * ======================================================================== */

export function SalaryRestrictedNotice({ page }: { page: string }): JSX.Element {
  const { t } = useTranslation();
  return (
    <InlineNotice tone="warning" role="note">
      <p>
        {t('pay.restricted.body', '{{page}} needs salary access, which nobody has by default.', { page })}
      </p>
      <p className="text-copy-sm">
        {t('pay.restricted.how', 'Ask an administrator to grant salary access in Settings → Roles & access.')}
      </p>
    </InlineNotice>
  );
}

export function SalaryGate({
  page,
  embedded = false,
  children,
}: {
  /** What the screen is called, for the sentence: "Payroll prep", "The labour cost report". */
  page: string;
  /** Inside another page (the smart report): the notice alone, no page shell. */
  embedded?: boolean;
  children?: ReactNode;
}): JSX.Element {
  const { t } = useTranslation();
  const allowed = useCan(sensitiveViewKey('salary'));
  if (allowed) return <>{children}</>;
  if (embedded) return <SalaryRestrictedNotice page={page} />;
  return (
    <PageShell title={t('pay.restricted.title', 'Restricted')}>
      <SalaryRestrictedNotice page={page} />
    </PageShell>
  );
}
